import { Alert, ArrowLeft, Dropdown, LeaderBoard, Typography } from '@dreamteam-monorepo/ui';
import type { LeaderBoardRow } from '@dreamteam-monorepo/ui';
import { useNavigate } from 'react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { LeaderboardEntry } from '@/lib/network';
import { useCurrentTournamentCalendar } from '@/hooks/useCurrentTournamentCalendar';
import { useLeaderboardPeriods } from '@/hooks/useLeaderboardPeriods';
import { useFetchLeaderboard } from '@/hooks/useFetchLeaderboard';
import { ContentAdSlot } from '@/ads';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';

const LEADERBOARD_PAGE_SIZE = 50;
const OVERALL_VALUE = 'overall';

const VIEWABLE_STATUSES = new Set(['locked', 'live', 'completed', 'finalised']);

const isViewableStatus = (status: string | undefined): boolean =>
  !!status && VIEWABLE_STATUSES.has(status);

function buildCellClickHandler(
  navigate: ReturnType<typeof useNavigate>,
  teamId?: string,
  gameweekId?: string,
  leaderboardType?: string,
  pageType?: string
) {
  return () => {
    const params = new URLSearchParams();
    if (teamId) params.set('teamId', teamId);
    if (gameweekId) params.set('gameweekId', gameweekId);
    if (leaderboardType) params.set('leaderboardType', leaderboardType);
    if (pageType) params.set('pageType', pageType);
    navigate(`/my-team?${params.toString()}`);
  };
}

function toLeaderBoardRow(
  entry: LeaderboardEntry,
  showBooster: boolean,
  selectedPeriodId: string,
  isOverallSelected: boolean,
  navigate: ReturnType<typeof useNavigate>,
  selectedPeriodStatus?: string,
  latestViewablePeriodId?: string,
  variant: LeaderBoardRow['variant'] = 'default'
): LeaderBoardRow {
  let targetGameweekId: string | undefined;
  let clickable = false;

  if (isOverallSelected) {
    targetGameweekId = latestViewablePeriodId;
    clickable = !!latestViewablePeriodId;
  } else {
    targetGameweekId = selectedPeriodId;
    clickable = isViewableStatus(selectedPeriodStatus);
  }

  const row: LeaderBoardRow = {
    variant,
    rank: String(entry.team_rank),
    teamId: entry.team_id,
    teamName: entry.team_name,
    managerName: entry.user_display_name,
    gameweekPoints: String(Math.round(entry.gameweek_points ?? entry.matchday_points ?? 0)),
    average: entry.average_points.toFixed(1),
    total: String(Math.round(entry.total_points)),
    booster: showBooster && entry.booster_used,
    gameweekId: targetGameweekId,
    leaderboardType: isOverallSelected ? 'overall' : 'per_gameweek',
    pageType: 'readonly',
  };

  if (clickable) {
    row.onCellClick = buildCellClickHandler(
      navigate,
      entry.team_id,
      targetGameweekId,
      row.leaderboardType,
      row.pageType
    );
  }

  return row;
}

export const LeaderboardPage = () => {
  const navigate = useNavigate();
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>(OVERALL_VALUE);

  const { data: activeTournament } = useCurrentTournamentCalendar();
  const tournamentCalendarId = activeTournament?.id ?? null;

  const { data: periodsData } = useLeaderboardPeriods(tournamentCalendarId);

  const periodType = periodsData?.type ?? 'gameweek';
  const periodLabel = periodType === 'matchday' ? 'Matchday' : 'Gameweek';
  const pointsColumnLabel = periodType === 'matchday' ? 'MD PTS' : 'GW PTS';

  const isPeriodSelected = selectedPeriodId !== OVERALL_VALUE;

  const leaderboardParams = tournamentCalendarId
    ? {
        tournament_calendar: tournamentCalendarId,
        type: periodType,
        limit: LEADERBOARD_PAGE_SIZE,
        ...(isPeriodSelected && periodType === 'matchday'
          ? { upto_matchday_id: selectedPeriodId }
          : {}),
        ...(isPeriodSelected && periodType === 'gameweek'
          ? { upto_gameweek_id: selectedPeriodId }
          : {}),
      }
    : null;

  const {
    data: leaderboardPages,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchLeaderboard(leaderboardParams);

  const pages = leaderboardPages?.pages.map(p => p.data) ?? [];
  const entries: LeaderboardEntry[] = useMemo(() => pages.flatMap(page => page.entries), [pages]);
  const userPosition = pages.find(page => page.user_position)?.user_position ?? null;
  const lastUpdatedRaw = pages[pages.length - 1]?.last_updated ?? null;

  // ── Infinite scroll: fetch the next page once the sentinel enters view ──
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const isOverallSelected = selectedPeriodId === OVERALL_VALUE;

  const selectedPeriodStatus = useMemo(() => {
    if (isOverallSelected) return undefined;
    return periodsData?.periods.find(p => p.id === selectedPeriodId)?.status;
  }, [periodsData?.periods, selectedPeriodId, isOverallSelected]);

  const latestViewablePeriodId = useMemo(() => {
    const viewablePeriods = periodsData?.periods.filter(p => isViewableStatus(p.status)) ?? [];
    if (viewablePeriods.length === 0) return undefined;
    // The periods are ordered by number ascending, so the last one is the latest
    return viewablePeriods[viewablePeriods.length - 1].id;
  }, [periodsData?.periods]);

  const rows: LeaderBoardRow[] = entries.map(e =>
    toLeaderBoardRow(
      e,
      isPeriodSelected,
      selectedPeriodId,
      isOverallSelected,
      navigate,
      selectedPeriodStatus,
      latestViewablePeriodId
    )
  );

  if (userPosition) {
    const userRow = toLeaderBoardRow(
      userPosition,
      isPeriodSelected,
      selectedPeriodId,
      isOverallSelected,
      navigate,
      selectedPeriodStatus,
      latestViewablePeriodId,
      'myScore'
    );
    const idx = rows.findIndex(r => r.rank === userRow.rank);
    if (idx === -1) {
      rows.unshift(userRow);
    } else {
      rows[idx] = userRow;
    }
  }

  const lastUpdated = lastUpdatedRaw
    ? new Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(new Date(lastUpdatedRaw))
    : null;

  const dropdownOptions = [
    { id: OVERALL_VALUE, label: 'Overall', value: OVERALL_VALUE },
    ...(periodsData?.periods ?? [])
      .filter(row => row.status !== 'open')
      .map(p => ({
        id: p.id,
        label: `${periodLabel} ${p.number}`,
        value: p.id,
      })),
  ];

  return (
    <div className="components light flex flex-col w-full">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="bg-white w-full">
        <div className="max-w-[846px] mx-auto w-full flex flex-col gap-[--padding-md] md:gap-[--padding-xl] pt-[--padding-xl] pb-[--padding-md] md:pb-[--padding-2xl] px-[--padding-md]">
          <button
            type="button"
            onClick={() => {
              trackFantasyEvent('leagueLink', { link_name: 'Leagues' });
              navigate('/leagues');
            }}
            className="flex items-center gap-2 self-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 rounded-[--corner-xs]"
          >
            <ArrowLeft className="size-6 text-[--color-blue-200]" />
            <Typography as="span" variant="button020" className="text-[--color-blue-200]">
              Leagues
            </Typography>
          </button>

          {/* On desktop: title and controls sit in one row */}
          <div className="flex flex-col gap-[--padding-md] md:flex-row md:items-center md:justify-between">
            <Typography
              as="h1"
              variant="headline050"
              className="text-[--page-header-header] !font-[900] md:!text-40 md:!leading-44"
            >
              LEADERBOARD
            </Typography>

            <div className="flex items-center justify-between md:justify-end gap-[--padding-sm] md:gap-[--padding-xl]">
              <div className="min-w-0">
                {lastUpdated && (
                  <>
                    <Typography
                      as="p"
                      variant="paragraph010"
                      className="text-[--color-neutral-900]"
                    >
                      LAST UPDATED:
                    </Typography>
                    <Typography
                      as="p"
                      variant="paragraph010"
                      className="!font-bold text-[--color-neutral-900]"
                    >
                      {lastUpdated}
                    </Typography>
                  </>
                )}
              </div>
              <Dropdown
                options={dropdownOptions}
                label=""
                values={[selectedPeriodId]}
                onValueChange={option => setSelectedPeriodId(option.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Leaderboard table ────────────────────────────────────────────── */}
      <div className="max-w-[846px] mx-auto w-full pb-10 md:pb-12">
        {isLoading || !tournamentCalendarId ? (
          <div className="flex w-full items-center justify-center py-16 text-gray-400">
            <Typography variant="paragraph020">Loading leaderboard…</Typography>
          </div>
        ) : isError ? (
          <div className="flex w-full items-center justify-center py-16 text-red-500">
            <Typography variant="paragraph020">Failed to load leaderboard.</Typography>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex w-full items-center justify-center py-16 text-gray-400">
            <Typography variant="paragraph020">No entries yet.</Typography>
          </div>
        ) : (
          <>
            {!isOverallSelected && !isViewableStatus(selectedPeriodStatus) && (
              <div className="px-[--padding-md] pb-[--padding-sm]">
                <Alert
                  title="Team view not available"
                  description="Team selections are only visible for locked, live or finalised gameweeks."
                  state="default"
                />
              </div>
            )}
            <LeaderBoard
              rows={rows}
              showAverage={!isPeriodSelected}
              showGameweekPoints
              labelProps={{ gameweekPointsLabel: pointsColumnLabel }}
            />
            {/* Sentinel: triggers loading the next page as it scrolls into view */}
            <div ref={sentinelRef} />
            {isFetchingNextPage && (
              <div className="flex w-full items-center justify-center py-6 text-gray-400">
                <Typography variant="paragraph020">Loading more…</Typography>
              </div>
            )}
          </>
        )}
      </div>
      <ContentAdSlot id="ad-boa-leaderboard" className="bg-white" />
    </div>
  );
};
